/**
 * Screening criteria — versioned, immutable once referenced by a decision
 * (invariant 8). "Edit" always publishes a new version; there is no update
 * route to call, which is what actually enforces immutability here (see
 * `createScreeningCriterionVersion` in modules/applications/service.ts).
 *
 * Starts empty in every environment until the firm supplies real criteria
 * (see CLAUDE.md's Applications section) — the empty state below is the
 * expected, common case today, not an error.
 */

import { useState } from 'react';
import { useInvalidate, useList } from '@refinedev/core';
import { api } from '../../../lib/api.js';
import { useCan } from '../../../lib/capabilities.js';

interface ScreeningCriterion {
  id: string;
  code: string;
  version: number;
  description: string;
  isPublished: boolean;
}

export function ScreeningCriterionList() {
  const invalidate = useInvalidate();
  const canWrite = useCan('screening-criteria:write');

  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useList<ScreeningCriterion>({
    resource: 'staff/screening-criteria',
    pagination: { mode: 'off' },
  });

  const rows = data?.data ?? [];
  // Latest version per code first — same ordering the API already returns
  // (code asc, version desc), grouped here only for display.
  const byCode = new Map<string, ScreeningCriterion[]>();
  for (const row of rows) {
    const list = byCode.get(row.code) ?? [];
    list.push(row);
    byCode.set(row.code, list);
  }

  async function publish() {
    if (!code.trim() || !description.trim()) return;
    setError(null);
    setBusy(true);
    try {
      await api('/staff/screening-criteria', {
        method: 'POST',
        body: JSON.stringify({ code: code.trim(), description: description.trim() }),
      });
      setCode('');
      setDescription('');
      await invalidate({ resource: 'staff/screening-criteria', invalidates: ['list'] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not publish that criterion.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="stack">
      <h1>Screening criteria</h1>
      <p className="muted">
        Shown to applicants during pre-qualification and available as decline reasons — every
        decline points at exactly one of these, never free text (invariant 8).
      </p>

      {isLoading ? <p className="muted">Loading…</p> : null}

      {rows.length === 0 && !isLoading ? (
        <p className="muted">Nothing published yet.</p>
      ) : (
        <div className="stack">
          {[...byCode.entries()].map(([groupCode, versions]) => (
            <div key={groupCode} className="card">
              <h3>{groupCode}</h3>
              <ul className="stack" style={{ gap: '0.3rem' }}>
                {versions.map((v) => (
                  <li key={v.id}>
                    <span className="pill">v{v.version}</span> {v.description}
                    {!v.isPublished ? <span className="muted"> (unpublished)</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {canWrite ? (
        <div className="card stack">
          <h2>Publish a version</h2>
          <p className="muted">
            An existing code publishes a new version of the same criterion; a new code starts a new
            one.
          </p>
          {error ? <p className="alert alert--error">{error}</p> : null}
          <input
            className="field__input"
            placeholder="Code, e.g. MIN_INCOME"
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
          <textarea
            className="field__input"
            rows={3}
            placeholder="Description shown to applicants and staff"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <button type="button" className="button" disabled={busy} onClick={() => void publish()}>
            {busy ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      ) : null}
    </section>
  );
}
